import React, { useContext, useEffect, useState } from 'react';
import { Tab, Table, Tabs } from 'react-bootstrap';
import { withTranslation, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AIConfigButton from '../../../components/AI/AIConfigButton';
import { LS_AI_CONFIG_KEY, LS_AUTHORITY_KEY } from '../../../core/constants/common';
import AICopilotConfigService from '../../../core/service/AICopilotConfigService';
import "./ProfileSettings.scss";
import { LoaderContext } from '../../../App';
import useHasPermission from '../../../core/hooks/useHasPermission';

const AIConfiguration = ({ t }) => {
  const [activeConfig, setActiveConfig] = useState({});
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [roles, setRoles] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [wordCounts, setWordCounts] = useState([]);
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsData.profile.company.id;
  const { k, i18n } = useTranslation();
  const [narratedBy, setNarratedBy] = useState("");
  const [audience, setAudience] = useState("");
  const [selectedLength, setSelectedLength] = useState("");
  const { hasSettingsUpdatePermission } = useHasPermission();

  useEffect(() => {
    fetchAllParams();
    fetchActiveConfig();
  }, []);

  const fetchAllParams = () => {
    AICopilotConfigService.getStyless().then(res => {
      if (res.status !== 200) return;
      setRoles(res.data.params.filter(p => p.paramType === "ROLE"));
      setAudiences(res.data.params.filter(p => p.paramType === "AUDIENCE"));
      setWordCounts(res.data.params.filter(p => p.paramType === "WORD_COUNT"));
    }).catch(err => { });
  };

  const fetchActiveConfig = (refetch = true) => {
    setLoaderDisplay(true);
    AICopilotConfigService.getActiveConfig()
      .then(res => {
        setLoaderDisplay(false);
        if (res.status !== 200) return;
        if (res.data?.id > 0) {
          setActiveConfig(res.data ?? { config: {} });
          updateConfigInLS(res.data?.config ?? {});

          // Set values from the config for syncing UI
          setNarratedBy(res.data.config?.narratedBy?.type || "");
          setAudience(res.data.config?.audience?.type || "");
          setSelectedLength(res.data.config?.length?.type || "");
        }
      })
      .catch(err => {
        setLoaderDisplay(false);
        if (err?.response?.status === 404 && refetch) {
          AICopilotConfigService.applyDeafultConfig(companyId).then(() => fetchActiveConfig(false));
        }
      });
  };

  const paramButton = (p, pIndex, selectedType, onSelect, typeKey) => (
    <AIConfigButton
      id={`checkbutton-${p.type}-${pIndex}`}
      key={`checkbutton-${p.type}-${pIndex}`}
      name={p.name}
      value={p.type}
      checked={selectedType === p.type}
      classes={selectedType === p.type ? 'Copilatcheckbox-btn Selected' : 'Copilatcheckbox-btn'}
      onCheck={() => {
        onSelect(p.type);
        const config = JSON.parse(JSON.stringify(activeConfig));
        config.config[typeKey] = { type: p.type, paramType: p.paramType };
        setActiveConfig(config);
      }}
    />
  );

  const wordCountButton = (p, pIndex) => (
    <AIConfigButton
      id={`checkbutton-${p.type}-${pIndex}`}
      key={`checkbutton-${p.type}-${pIndex}`}
      name={p.name}
      value={p.type}
      checked={selectedLength === p.type}
      classes={selectedLength === p.type ? 'Copilatcheckbox-btn Selected' : 'Copilatcheckbox-btn'}
      onCheck={() => {
        setSelectedLength(p.type);
        const config = JSON.parse(JSON.stringify(activeConfig));
        config.config.length = { type: p.type, paramType: "WORD_COUNT" };
        setActiveConfig(config);
      }}
    />
  );

  const onApplyConfig = () => {
    const configToSave = {
      length: activeConfig.config?.length || { type: selectedLength, paramType: "WORD_COUNT" },
      narratedBy: activeConfig.config?.narratedBy || { type: narratedBy, paramType: "ROLE" },
      audience: activeConfig.config?.audience || { type: audience, paramType: "AUDIENCE" }
    };

    AICopilotConfigService.saveConfig(configToSave)
      .then(res => {
        if (res.status === 200) {
          toast.success("Configuration saved successfully");
          updateConfigInLS(configToSave);
          fetchActiveConfig(false);
        } else {
          toast.error("Failed to save configuration");
        }
      })
      .catch(() => {
        toast.error("Error occurred while saving");
      });
  };

  const updateConfigInLS = (config) => {
    localStorage.setItem(LS_AI_CONFIG_KEY, JSON.stringify(config));
  };

  return (
    <div className='ProfileSettings'>
      <div className="List_header pb-2"> <h5 className="MH m-0">{t('ESGAICopilot.label')}</h5></div>
      <div className='row  m-0'>
        <div className='col-lg-12 p-0 rep-btn'>
          <Tabs
            defaultActiveKey="Content_generation"
            id="uncontrolled-tab-example"
            className="Companysettings-tab">
            <Tab eventKey="Content_generation" title={t("ContentGeneration.label")}>
              <div className='row  m-0'>
                <div className='col-lg-12 p-2'>
                  <p className='px-2  mb-0'>{t('AIConfigHelper4.label')}</p>
                </div>
                <div className='col-lg-12 col-sm-12 p-2'>
                  <div className="Personality_card_main card">
                    <div className="card-body p-2">
                      <div className='ai-config-section'>
                        <div className='ai-config-section-header'>
                          <h6>{t('NarratedBy.label')}</h6>
                        </div>
                        <div className="ai-config-section-buttons">
                          {roles.map((p, pIndex) =>
                            paramButton(p, pIndex, narratedBy, setNarratedBy, "narratedBy")
                          )}
                        </div>
                      </div>

                      <div className='ai-config-section'>
                        <div className='ai-config-section-header'>
                          <h6>{t('Audience.label')}</h6>
                        </div>
                        <div className="ai-config-section-buttons">
                          {audiences.map((p, pIndex) =>
                            paramButton(p, pIndex, audience, setAudience, "audience")
                          )}
                        </div>
                      </div>

                      <div className='ai-config-section'>
                        <div className='ai-config-section-header'>
                          <h6>{t('Length.label')}</h6>
                        </div>
                        <div className="ai-config-section-buttons">
                          {wordCounts.map((p, pIndex) =>
                            wordCountButton(p, pIndex)
                          )}
                        </div>
                      </div>

                      <div className='mt-1 mb-1 text-center'>
                        <button
                          type="submit"
                          hidden={!hasSettingsUpdatePermission()}
                          className="btn btn-primary px-3"
                          onClick={onApplyConfig}
                        >
                          {t("Apply.label")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab eventKey="Chat_data" title={t("ChatWithData.label")}>
              <div className='p-2 py-3'>
                <div className='p-3 text-center'>
                  {t("NoDataAvailable.label")}
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(AIConfiguration);




